import { useState } from "react";

import type { SquadsMultisigInfo, SquadsMultisigMember, SquadsSignerStatus } from "@/hooks/useSquadsMultisig";
import { deriveSignerStatus, useSquadsMultisig } from "@/hooks/useSquadsMultisig";

import type { PositionSourceDetection } from "../positionSourceDetection";
import { detectPositionSource } from "../positionSourceDetection";
interface UsePositionSourceResult {
    sourceInput: string;
    sourceDetection: PositionSourceDetection;
    sourceKind: "eoa" | "multisig-squads" | null;
    resolvedMultisig: SquadsMultisigInfo | null;
    memberCanVerify: boolean;
    membershipCheckPending: boolean;
    membershipUnknown: boolean;
    walletNeedsSwitch: boolean;
    multisigSummary: {
        address: string;
        vaultAddress: string | undefined;
        threshold: number;
        memberCount: number;
        members: SquadsMultisigMember[] | undefined;
        signerStatus: SquadsSignerStatus;
    } | null;
    handleSourceRetry: () => void;
}

export function usePositionSource(address: string, connectedWalletAddress: string): UsePositionSourceResult {
    const [sourceRetryNonce, setSourceRetryNonce] = useState(0);
    const sourceInput = address.trim();

    const multisigStatus = useSquadsMultisig(sourceInput, null, sourceRetryNonce);
    const sourceDetection = detectPositionSource(sourceInput, multisigStatus);
    const sourceKind = sourceDetection.sourceKind;
    // Connected, but with a wallet other than the EOA source the user entered.
    // You can only sign with the wallet you're connected as, so "Verify
    // Ownership" can't run — instead the action becomes "Switch Wallet" (opens
    // the wallet modal to connect the matching wallet) rather than a dead button.
    const eoaSourceMismatch =
        sourceKind === "eoa" &&
        connectedWalletAddress.length > 0 &&
        address.trim().length > 0 &&
        address.trim() !== connectedWalletAddress;
    const resolvedMultisig = sourceDetection.status === "multisig-squads" ? sourceDetection.multisig : null;
    const needsSignerScopedMultisigPreflight =
        connectedWalletAddress.length > 0 && resolvedMultisig !== null && resolvedMultisig.members === undefined;
    const signerScopedMultisigStatus = useSquadsMultisig(
        needsSignerScopedMultisigPreflight ? resolvedMultisig.address : "",
        connectedWalletAddress || null
    );
    const fallbackSignerStatus =
        resolvedMultisig !== null &&
        resolvedMultisig.members === undefined &&
        signerScopedMultisigStatus.status === "success" &&
        signerScopedMultisigStatus.multisig.address === resolvedMultisig.address
            ? signerScopedMultisigStatus.signerStatus
            : null;
    const multisigSignerStatus =
        resolvedMultisig !== null && connectedWalletAddress.length > 0
            ? resolvedMultisig.members !== undefined
                ? deriveSignerStatus(resolvedMultisig.members, connectedWalletAddress)
                : fallbackSignerStatus
            : "no-signer";
    const membershipCheckPending =
        resolvedMultisig !== null &&
        connectedWalletAddress.length > 0 &&
        resolvedMultisig.members === undefined &&
        (signerScopedMultisigStatus.status === "idle" || signerScopedMultisigStatus.status === "loading");
    const membershipUnknown =
        resolvedMultisig !== null &&
        connectedWalletAddress.length > 0 &&
        resolvedMultisig.members === undefined &&
        !membershipCheckPending &&
        fallbackSignerStatus === null;
    const memberCanVerify =
        sourceKind !== "multisig-squads" || multisigSignerStatus === "can-verify" || membershipUnknown;
    // Connected with a wallet that can't sign for this multisig — not a member,
    // or a member without Vote/Execute. Mirrors `eoaSourceMismatch`: rather than
    // a dead, disabled button, the action becomes "Switch Wallet" so the user can
    // connect a member wallet that can verify. Excludes the still-checking and
    // membership-unknown states (those stay actionable; the API asserts on submit).
    const multisigSignerMismatch =
        sourceKind === "multisig-squads" &&
        connectedWalletAddress.length > 0 &&
        resolvedMultisig !== null &&
        (multisigSignerStatus === "not-member" || multisigSignerStatus === "cannot-verify");
    // A connected wallet that can't act as the source — either an EOA mismatch or
    // a multisig non-signer. Both relabel the verify button to "Switch Wallet"
    // and route its click through the disconnect-and-reconnect handler.
    const walletNeedsSwitch = eoaSourceMismatch || multisigSignerMismatch;
    const multisigSummary =
        resolvedMultisig !== null
            ? {
                  address: resolvedMultisig.address,
                  vaultAddress: resolvedMultisig.vaultAddress,
                  threshold: resolvedMultisig.threshold,
                  memberCount: resolvedMultisig.memberCount,
                  members: resolvedMultisig.members,
                  signerStatus: multisigSignerStatus ?? "no-signer"
              }
            : null;

    function handleSourceRetry(): void {
        setSourceRetryNonce((n) => n + 1);
    }
    return {
        sourceInput,
        sourceDetection,
        sourceKind,
        resolvedMultisig,
        memberCanVerify,
        membershipCheckPending,
        membershipUnknown,
        walletNeedsSwitch,
        multisigSummary,
        handleSourceRetry
    };
}
